import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, Save, Copy, RefreshCw, Wand2, Zap, AlertCircle, ExternalLink, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import VosaBackfillWizard from "./VosaBackfillWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VOSA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/integrations/vosa`;

const timeAgo = (iso) => {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

/**
 * VosaSection — Vosa XML feed field-mapping table + downloadable feed CTA.
 * Mirrors the Rightmove mapping UX: one row per Vosa field, admin picks a
 * CMS field OR sets a manual value.
 */
export default function VosaSection() {
  const [fields, setFields] = useState([]);
  const [cmsFields, setCmsFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publicPath, setPublicPath] = useState("");
  const [rotating, setRotating] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [snap, setSnap] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [showUnmapped, setShowUnmapped] = useState(false);

  const loadPublicUrl = async () => {
    try {
      const r = await axios.get(`${VOSA_BASE}/public-url`, { withCredentials: true });
      setPublicPath(r.data.path || "");
    } catch (_) {
      /* silent — token endpoint is best-effort */
    }
  };

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${VOSA_BASE}/feed-status`, { withCredentials: true });
      setSnap(r.data.last_feed_snapshot || null);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([
          axios.get(`${VOSA_BASE}/mapping`, { withCredentials: true }),
          axios.get(`${API}/admin/integrations/rightmove-mapping`, { withCredentials: true })
            .catch(() => ({ data: { cms_fields: [] } })),
        ]);
        setFields(m.data.fields || []);
        setCmsFields(c.data.cms_fields || []);
        loadPublicUrl();
        loadStatus();
      } catch (e) {
        toast.error("Failed to load Vosa mapping");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await axios.post(`${VOSA_BASE}/regenerate`, {}, { withCredentials: true });
      setSnap(r.data);
      toast.success(
        `Regenerated · ${r.data.property_count} properties · ${(r.data.size_bytes / 1024).toFixed(1)} KB`,
      );
    } catch (_) {
      toast.error("Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const rotateToken = async () => {
    if (!window.confirm("Rotate the feed token? The old URL will stop working immediately.")) return;
    setRotating(true);
    try {
      const r = await axios.post(`${VOSA_BASE}/rotate-feed-token`, {}, { withCredentials: true });
      setPublicPath(r.data.path || "");
      toast.success("Feed token rotated");
    } catch (_) {
      toast.error("Rotate failed");
    } finally {
      setRotating(false);
    }
  };

  const copyPublicUrl = async () => {
    const full = `${process.env.REACT_APP_BACKEND_URL}${publicPath}`;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Public feed URL copied");
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const openPublicUrl = async () => {
    if (!publicPath) return;
    const full = `${process.env.REACT_APP_BACKEND_URL}${publicPath}`;
    // Copy to clipboard AND open in a new tab.
    try { await navigator.clipboard.writeText(full); } catch (_) { /* ignore */ }
    window.open(full, "_blank", "noopener,noreferrer");
    toast.success("URL copied · opening in a new tab");
  };

  const updateField = (idx, patch) => {
    setFields((prev) => {
      const next = prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
      scheduleAutosave(next);
      return next;
    });
  };

  // Debounced autosave — fires 600ms after the last edit so consecutive keystrokes
  // don't spam the backend. Save button still available for explicit persistence.
  const autosaveTimer = useRef(null);
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle | saving | saved | error
  const scheduleAutosave = (nextFields) => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setAutosaveStatus("saving");
    autosaveTimer.current = setTimeout(async () => {
      try {
        const mapping = {};
        for (const f of nextFields) {
          mapping[f.vosa_field] = { value: f.value || "", cms_field: f.cms_field || "" };
        }
        await axios.put(`${VOSA_BASE}/mapping`, { mapping }, { withCredentials: true });
        setAutosaveStatus("saved");
        setTimeout(() => setAutosaveStatus((s) => (s === "saved" ? "idle" : s)), 1500);
      } catch (_) {
        setAutosaveStatus("error");
      }
    }, 600);
  };

  const save = async () => {
    setSaving(true);
    try {
      const mapping = {};
      for (const f of fields) {
        mapping[f.vosa_field] = { value: f.value || "", cms_field: f.cms_field || "" };
      }
      await axios.put(`${VOSA_BASE}/mapping`, { mapping }, { withCredentials: true });
      toast.success("Vosa mapping saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadFeed = async () => {
    try {
      const res = await axios.get(`${VOSA_BASE}/feed.xml`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `vosa_feed_${today}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Vosa XML feed downloaded");
    } catch (e) {
      toast.error("Feed download failed");
    }
  };

  const downloadEmptyFeed = async () => {
    try {
      const res = await axios.get(`${VOSA_BASE}/feed-empty.xml`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `vosa_feed_empty_${today}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Blank Vosa XML feed downloaded");
    } catch (e) {
      toast.error("Blank feed download failed");
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const mapped = fields.filter((f) => f.cms_field || f.value).length;
  const total = fields.length;
  const coverage = total ? Math.round((mapped / total) * 100) : 0;
  const unmapped = fields.filter((f) => !f.cms_field && !f.value);

  // Group rows by their Vosa `group` for readability.
  const groups = {};
  fields.forEach((f, i) => {
    (groups[f.group] = groups[f.group] || []).push({ ...f, _idx: i });
  });

  return (
    <div className="space-y-5" data-testid="vosa-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium">Vosa XML Feed</h3>
          <p className="text-xs text-muted-foreground">
            Map each Vosa XML field to a CMS field, or enter a manual override. The feed contains every LIVE property.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-muted-foreground">
            {mapped} / {total} mapped · <span className="font-medium">{coverage}%</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWizardOpen(true)}
            className="rounded-none"
            data-testid="vosa-backfill-btn"
          >
            <Wand2 className="h-4 w-4 mr-1" /> Backfill missing
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={regenerate}
            disabled={regenerating}
            className="rounded-none"
            data-testid="vosa-regenerate-btn"
          >
            <Zap className={`h-4 w-4 mr-1 ${regenerating ? "animate-pulse" : ""}`} />
            {regenerating ? "Regenerating…" : "Regenerate feed"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadFeed}
            className="rounded-none"
            data-testid="vosa-download-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Download XML
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadEmptyFeed}
            className="rounded-none"
            data-testid="vosa-download-empty-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Download blank XML
          </Button>
          <div
            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            data-testid="vosa-autosave-status"
          >
            {autosaveStatus === "saving" && <><RefreshCw className="h-3 w-3 animate-spin" /> Saving…</>}
            {autosaveStatus === "saved"  && <><Check className="h-3 w-3 text-emerald-600" /> Saved</>}
            {autosaveStatus === "error"  && <span className="text-red-600">Save failed</span>}
            {autosaveStatus === "idle"   && <span className="opacity-60">Autosave on</span>}
          </div>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="rounded-none"
            data-testid="vosa-save-btn"
          >
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save mapping"}
          </Button>
        </div>
      </div>

      {/* Last generated + unmapped fields summary strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-muted/10 px-3 py-2 text-xs">
        <div className="flex items-center gap-4" data-testid="vosa-status-strip">
          <span>
            <span className="text-muted-foreground">Last generated:</span>{" "}
            <span className="font-medium" data-testid="vosa-last-generated">
              {snap ? timeAgo(snap.generated_at) : "never"}
            </span>
            {snap && (
              <span className="text-muted-foreground ml-2">
                · {snap.property_count} properties · {(snap.size_bytes / 1024).toFixed(1)} KB
              </span>
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowUnmapped((s) => !s)}
          className="flex items-center gap-1 text-amber-700 hover:underline"
          data-testid="vosa-toggle-unmapped"
        >
          <AlertCircle className="h-3.5 w-3.5" />
          {unmapped.length} unmapped Vosa field{unmapped.length === 1 ? "" : "s"}
          <span className="text-muted-foreground ml-1">
            ({showUnmapped ? "hide" : "show"})
          </span>
        </button>
      </div>

      {showUnmapped && unmapped.length > 0 && (
        <div
          className="border border-amber-300 bg-amber-50 p-3 text-xs space-y-2"
          data-testid="vosa-unmapped-panel"
        >
          <div className="font-medium text-amber-900">
            These Vosa fields have no CMS field mapped and no manual value:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {unmapped.map((f) => (
              <span
                key={f.composite_key || f.vosa_field}
                className="inline-flex items-center gap-1 bg-white border border-amber-300 px-2 py-0.5 font-mono text-[11px]"
                data-testid={`vosa-unmapped-${f.vosa_field}`}
              >
                <span className="text-muted-foreground uppercase text-[9px]">{f.group}</span>
                <span>{f.vosa_field}</span>
                {f.required && <span className="text-red-500">*</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Public feed URL — shareable, token-protected, unauthenticated */}
      <div className="border border-border bg-muted/20 p-3 space-y-2" data-testid="vosa-public-url-panel">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs">
            <div className="font-semibold uppercase tracking-wide text-muted-foreground">
              Public Feed URL
            </div>
            <div className="text-[11px] text-muted-foreground">
              Share this with Vosa&apos;s scheduler — token-protected, no admin cookie required.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={copyPublicUrl}
              disabled={!publicPath}
              className="rounded-none"
              data-testid="vosa-copy-url-btn"
            >
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openPublicUrl}
              disabled={!publicPath}
              className="rounded-none"
              data-testid="vosa-open-url-btn"
            >
              <ExternalLink className="h-4 w-4 mr-1" /> Copy &amp; open
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={rotateToken}
              disabled={rotating}
              className="rounded-none"
              data-testid="vosa-rotate-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${rotating ? "animate-spin" : ""}`} />
              Rotate token
            </Button>
          </div>
        </div>
        <Input
          readOnly
          value={publicPath ? `${process.env.REACT_APP_BACKEND_URL}${publicPath}` : ""}
          className="rounded-none h-8 text-xs font-mono bg-background"
          onFocus={(e) => e.target.select()}
          data-testid="vosa-public-url-input"
        />
      </div>

      {Object.entries(groups).map(([groupName, rows]) => (
        <div key={groupName} className="border border-border">
          <div className="bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide">
            {groupName}
          </div>
          <div className="grid grid-cols-[1fr_auto_1.5fr_2fr_2fr] gap-2 items-center px-3 py-2 text-xs font-medium text-muted-foreground border-b">
            <span>Vosa Field</span>
            <span className="w-16">Type</span>
            <span>Sample Value</span>
            <span>CMS Field</span>
            <span>Manual Value (overrides CMS)</span>
          </div>
          {rows.map((f) => (
            <div
              key={f.vosa_field}
              className="grid grid-cols-[1fr_auto_1.5fr_2fr_2fr] gap-2 items-center px-3 py-1.5 border-b border-border/50 text-sm"
              data-testid={`vosa-row-${f.vosa_field}`}
            >
              <span className="font-mono text-xs">
                {f.vosa_field}
                {f.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              <span className="w-16 text-[10px] uppercase text-muted-foreground">{f.type}</span>
              <span
                className="text-[11px] text-muted-foreground italic truncate"
                title={f.sample || ""}
                data-testid={`vosa-sample-${f.vosa_field}`}
              >
                {f.sample || <span className="opacity-50">—</span>}
              </span>
              <div className="flex items-center gap-1">
                <select
                  value={f.cms_field || ""}
                  onChange={(e) => updateField(f._idx, { cms_field: e.target.value })}
                  className="rounded-none h-8 border border-input bg-background px-2 text-xs flex-1 min-w-0"
                  data-testid={`vosa-cms-${f.vosa_field}`}
                >
                  <option value="">— none —</option>
                  {cmsFields.map((c) => (
                    <option key={c.key} value={c.key}>{c.label || c.key}</option>
                  ))}
                </select>
                {f.suggested_cms_field && f.suggested_cms_field !== f.cms_field && (
                  <button
                    type="button"
                    onClick={() => updateField(f._idx, { cms_field: f.suggested_cms_field })}
                    title={`Accept suggested CMS field: ${f.suggested_cms_field}`}
                    className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100"
                    data-testid={`vosa-suggest-${f.vosa_field}`}
                  >
                    <span className="text-emerald-500">+</span>
                    <span className="truncate max-w-[90px]">{f.suggested_cms_field}</span>
                  </button>
                )}
              </div>
              <Input
                value={f.value || ""}
                onChange={(e) => updateField(f._idx, { value: e.target.value })}
                placeholder="(leave blank to use CMS field)"
                className="rounded-none h-8 text-xs"
                data-testid={`vosa-val-${f.vosa_field}`}
              />
            </div>
          ))}
        </div>
      ))}

      <VosaBackfillWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onSaved={() => { /* wizard already toasts; reload not required */ }}
      />
    </div>
  );
}
